// Marketing landing — outcome before device. Section order (spec):
// 1 hero w/ financial claims · 2 benefit cards · 3 savings calculator ·
// 4 product intro (first device image) · 5 how it works · 6 local-first ·
// 7 capabilities · 8 Energy Intelligence · 9 integrations · 10 real results ·
// 11 pricing · 12 tech specs · 13 final CTA.
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { FileText, PiggyBank, ShieldCheck, TrendingUp } from "../../components/icons";
import { track, useSectionReached } from "../../lib/analytics";
import { HeroVisual } from "./HeroVisual";
import { LandingShell } from "./LandingShell";
import {
  Capabilities,
  EnergyIntelligenceSection,
  FinalCta,
  HowItWorks,
  Integrations,
  LocalFirst,
  ProductIntro,
  TechSpecs,
} from "./LandingSections";
import { PricingSection } from "./PricingSection";
import { SavingsCalculator } from "./SavingsCalculator";

function Hero() {
  const { t } = useTranslation();
  return (
    <section id="top" data-bg="energy" className="max-w-6xl mx-auto px-4 pt-14 pb-12">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        {/* Mobile: headline + claims BEFORE any visual (DOM order below). */}
        <div className="text-center lg:text-left">
          <h1 className="text-3xl md:text-5xl font-semibold leading-tight">
            {t("landing.heroTitle")}
          </h1>
          <p className="text-muted mt-4 text-base md:text-lg">{t("landing.heroSub")}</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div className="card p-4 text-left">
              <div className="inline-flex items-center gap-2 text-good text-xs font-medium">
                <PiggyBank size={14} />
                {t("landing.value.tariff.title")}
              </div>
              <div className="text-xl font-semibold mt-1">{t("landing.claimSave")}</div>
              <p className="text-xs text-muted mt-1">{t("landing.claimSaveDesc")}</p>
            </div>
            <div className="card p-4 text-left">
              <div className="inline-flex items-center gap-2 text-brand text-xs font-medium">
                <TrendingUp size={14} />
                {t("landing.value.arbitrage.title")}
              </div>
              <div className="text-xl font-semibold mt-1">{t("landing.claimEarn")}*</div>
              <p className="text-xs text-muted mt-1">{t("landing.claimEarnDesc")}</p>
            </div>
          </div>
          <p className="text-xs text-muted/80 mt-3 text-left">* {t("landing.claimDisclaimer")}</p>
          <div className="mt-6 flex items-center gap-3 flex-wrap justify-center lg:justify-start">
            <a
              href="#how-it-works"
              className="btn-primary text-base px-5 py-2.5"
              onClick={() => track("hero_cta_click", { source: "hero" })}
            >
              {t("landing.ctaHow")}
            </a>
            <a
              href="#pricing"
              className="btn-ghost text-base px-5 py-2.5"
              onClick={() => track("pricing_cta_click", { source: "hero" })}
            >
              {t("landing.ctaPricing")}
            </a>
          </div>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-muted">
            <ShieldCheck size={15} />
            {t("landing.heroLocal")}
          </p>
        </div>
        <HeroVisual />
      </div>
    </section>
  );
}

function ValueCards() {
  const { t } = useTranslation();
  const cards = [
    { key: "tariff", icon: <PiggyBank size={20} />, to: "/tariff-optimization", tone: "text-good" },
    { key: "arbitrage", icon: <TrendingUp size={20} />, to: "/battery-arbitrage", tone: "text-brand" },
    { key: "intelligence", icon: <FileText size={20} />, to: "/energy-intelligence", tone: "text-accent" },
  ] as const;
  return (
    <section className="max-w-6xl mx-auto px-4 grid gap-4 md:grid-cols-3">
      {cards.map((card) => (
        <div key={card.key} className="card p-6 flex flex-col">
          <div className={`inline-flex items-center gap-2 text-sm font-medium ${card.tone}`}>
            {card.icon}
            {t(`landing.value.${card.key}.title`)}
          </div>
          <div className="mt-3 text-2xl lg:text-3xl font-semibold leading-tight">
            {t(`landing.value.${card.key}.amount`)}
          </div>
          <p className="text-sm text-muted mt-3 flex-1">{t(`landing.value.${card.key}.desc`)}</p>
          <p className="text-xs text-muted/80 mt-4 border-t border-border pt-3">
            {t(`landing.value.${card.key}.footer`)}
          </p>
          <Link
            to={card.to}
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-brand hover:underline"
          >
            {t(`landing.value.${card.key}.cta`)}
            <ArrowRight size={14} />
          </Link>
        </div>
      ))}
    </section>
  );
}

function ProofSection() {
  const { t } = useTranslation();
  const metrics = [
    { value: t("landing.proof.m1"), label: t("landing.proof.m1Label") },
    { value: t("landing.proof.m2"), label: t("landing.proof.m2Label") },
    { value: t("landing.proof.m3"), label: t("landing.proof.m3Label") },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 py-14">
      <h2 className="text-2xl font-semibold text-center">{t("landing.proof.title")}</h2>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {metrics.map((m) => (
          <div key={m.label} className="card p-6 text-center">
            <div className="text-3xl lg:text-4xl font-semibold font-mono text-good">{m.value}</div>
            <div className="text-sm text-muted mt-2">{m.label}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted text-center mt-4 max-w-2xl mx-auto">
        {t("landing.proof.note")}
      </p>
    </section>
  );
}

export default function LandingPage() {
  const productRef = useSectionReached<HTMLElement>("product_section_reached");
  const pricingRef = useSectionReached<HTMLDivElement>("pricing_section_reached");

  return (
    <LandingShell>
      <Hero />
      <ValueCards />
      <SavingsCalculator />
      <ProductIntro ref={productRef} />
      <HowItWorks />
      <LocalFirst />
      <Capabilities />
      <EnergyIntelligenceSection />
      <Integrations />
      <ProofSection />
      <div ref={pricingRef} data-bg="plans">
        <PricingSection />
      </div>
      <TechSpecs />
      <FinalCta onCta={() => track("hero_cta_click", { source: "final" })} />
    </LandingShell>
  );
}
